import type { BasicCoveragesProps } from '@/components/car/BasicCoverages';
import type { BundlesProps } from '@/components/car/Bundles';
import type { DrivingExperienceProps, DrivingProps } from '@/components/car/Driving';
import type { ElectricCoveragesProps } from '@/components/car/ElectricCoverages';
import type { FaqProps } from '@/components/car/Faq';
import type { OverallCoveragesProps } from '@/components/car/OverallCoverages';
import type { ValuePropositionProps } from '@/components/fsd/ValueProposition';
import { FixedPriceCoverage } from '@/components/car/OverallCoverages';
import { YeahReally } from '@/components/car/OverallCoverages';
import {
  crackImg,
  hitImg,
  liabilityImg,
  roadsideAssistanceImg,
  theftImg,
  unexpectedImg,
} from '@/components/car/assets/coverages/basic';
import { batteryImg, chargerImg, wallChargerImg } from '@/components/car/assets/coverages/electric';
import {
  altColorEmergencyImg,
  altColorFairPricesImg,
  altColorTowingImg,
  claimsImg,
  emergencyImg,
  fairPricesImg,
  plantTreesImg,
  towingImg,
} from '@/components/car/assets/coverages/overall';
import type { CustomersReviewsProps } from '@/components/shared';
import { sharedConfig } from '@/pageConfig/shared';
import ogImage from '@/static/assets/car/og.png';
import type { BasePageConfig } from '../../BasePageConfig';
import { FSDHowItWorksProps } from '../../../components/fsd/FSDHowItWorks';

export interface AnalyticsConfig {
  page: 'car' | 'fsd';
  product: string;
  dataSource: string;
  ctaId: string;
  faqViewedEvent: string;
  videoClickedEvent?: string;
}

const productJsonld = {
  '@context': 'https://schema.org/',
  '@type': 'Product',
  name: 'Lemonade Car Insurance',
  image: [ogImage.src, fairPricesImg.src],
  description: 'Auto Insurance policies',
  brand: {
    '@type': 'Brand',
    name: 'Lemonade',
    logo: 'https://www.lemonade.com/assets/logo-e0f7c4f109ef5af2bb9604137856587ca61a56ed8c3ab2b8f56fcec923babafc.svg',
  },
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.9',
    ratingCount: '62818',
  },
  offers: {
    '@type': 'AggregateOffer',
    offerCount: '',
    lowPrice: '30',
    highPrice: '',
    priceCurrency: 'USD',
  },
};

export interface LowMilageConfig {
  variant: 'default' | 'ppm';
  title: string;
  subtitle: string;
}

export interface FooterConfig {
  belowCtaText?: string;
  paddingTop?: number | number[];
  paddingBottom?: number | number[];
}

export interface Config extends BasePageConfig {
  hero: DrivingProps;
  drivingExperience?: DrivingExperienceProps;
  overallCoverages: OverallCoveragesProps;
  lowMilage?: LowMilageConfig;
  basicCoverages: BasicCoveragesProps;
  bundles?: BundlesProps;
  customersReviews: CustomersReviewsProps;
  faq: FaqProps;
  electricCoverages?: ElectricCoveragesProps;
  valueProposition?: ValuePropositionProps;
  fsdHowItWorks?: FSDHowItWorksProps;
  showForest?: boolean;
  enableHeaderGreenButton?: boolean;
  uniclientRedirectUrl?: string;
  footer?: FooterConfig;
  ctaGlow?: boolean;
  analytics: AnalyticsConfig;
}

export const config: Config = {
  analytics: {
    page: 'car',
    product: 'car',
    dataSource: 'car',
    ctaId: 'gtm_button_car_check_prices',
    faqViewedEvent: 'car_faq_section.viewed',
    videoClickedEvent: 'car_page.video.clicked',
  },
  seo: {
    title: 'Car Insurance: Get an Auto Insurance Quote Online | Lemonade',
    metaTitle: 'Car Insurance: Get an Auto Insurance Quote Online | Lemonade',
    description:
      'Car insurance with low prices based on the way you actually drive! Customize your coverages and get discounts for bundling, low mileage, and much more.',
    ogImage: ogImage.src,
    advancedRobots: true,
    jsonld: [productJsonld],
  },
  layout: {
    ...sharedConfig['en-US'].layout,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-US'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-US'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-US'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
  },
  hero: {
    title: 'Car insurance that costs less\nfor safe drivers',
    subtitle: 'Super fast everything. Seamless claims. Low rates for low mileage.',
  },
  drivingExperience: {
    mode: 'default',
    showPlayButton: true,
  },
  overallCoverages: {
    priceType: 'fixed',
    darkBackground: false,
    title: 'A new kind of car insurance',
    subtitle: 'We use location-based technology to provide better, more affordable rates',
    coverages: [
      {
        id: FixedPriceCoverage.RoadsideAssistance,
        background: towingImg.src,
        altColorBackground: altColorTowingImg.src,
        title: 'Free roadside assistance',
        description: 'Tows, jump starts, tire changes,<br>and winching - on us!',
        alt: 'roadside assistance coverage',
        pill: {
          text: 'free',
        },
      },
      {
        id: FixedPriceCoverage.ClaimsResolution,
        background: claimsImg.src,
        title: 'Super fast claims resolution',
        description: 'We’ll have extra details to help us handle your claim quickly.',
        alt: 'Instant car insurance quotes online',
      },
      {
        id: FixedPriceCoverage.FairPrice,
        background: fairPricesImg.src,
        altColorBackground: altColorFairPricesImg.src,
        title: 'Fair prices, based on how you drive',
        description: 'Get a discount, and earn a lower premium as you drive better.',
        alt: 'auto insurance discounts',
      },
      {
        id: FixedPriceCoverage.Trees,
        background: plantTreesImg.src,
        title: 'We’ll plant trees to clean up after your car!',
        description: 'Let us shrink your car’s carbon footprint by planting trees.',
        alt: 'environment friendly car insurance',
        pill: {
          text: 'whoa',
          backgroundColor: 'green',
        },
        preElement: <YeahReally />,
      },

      {
        id: FixedPriceCoverage.EmergencyCrash,
        background: emergencyImg.src,
        altColorBackground: altColorEmergencyImg.src,
        title: 'Emergency crash assistance',
        description: 'We’ll know if you’re in an accident and get you the help you need.',
        alt: 'car accident assistance and coverage with your car insurance',
        pill: {
          text: 'free',
        },
      },
    ],
  },
  lowMilage: {
    variant: 'default',
    title: 'Low mileage drivers pay less',
    subtitle:
      "Lemonade's car insurance prices factor in how much you drive.\nDrive less, pay less–so you can cut costs and emissions!",
  },
  electricCoverages: {
    title: 'EV and hybrid drivers\npay even less',
    subtitle:
      'Environment-friendly cars dramatically reduce carbon dioxide emissions,\nso we added some special coverages and discounts for EVs and hybrids.',
    data: [
      {
        background: chargerImg.src,
        title: 'Emergency charging',
        description: 'If you’re stuck with a drained battery we’ll send a charging truck your way.',
        alt: 'additional car insurance coverages',
      },
      {
        background: batteryImg.src,
        title: 'EV/Hybrid discounts',
        description: 'A bonus discount to thank environment-friendly drivers.',
        alt: 'electric and hybrid insurance discounts',
      },
      {
        background: wallChargerImg.src,
        title: 'Charger coverage',
        description: 'If something happens to your wall or portable charger we’ll help replace it.',
        alt: 'auto insurance for electric vehicles and hybrid cars',
      },
    ],
  },
  basicCoverages: {
    title: 'What’s covered',
    subtitle: 'Whether it’s a fender bender or total loss, our team and app are ready to help!',
    darkBackground: true,
    coverages: [
      {
        background: hitImg.src,
        title: 'If you’re in a<br/>car crash',
        description: "We'll cover, or recover, the costs to repair the damage.",
        alt: 'car crash insurance',
      },
      {
        background: roadsideAssistanceImg.src,
        title: 'If you’re stalled on the highway',
        description: 'We include roadside assistance for tows, jumps, and more.',
        alt: 'flat tire, towing and jumps insurance coverage',
      },
      {
        background: unexpectedImg.src,
        title: 'If the unexpected happens',
        description: 'We’ll cover damage from fires, weather, vandalism, and more.',
        alt: 'Car damage insurance',
      },
      {
        background: crackImg.src,
        title: 'If your glass<br/>is damaged',
        description: 'We’ll cover repairs for windshields, moonroofs, and more',
        alt: 'windshield and sunroof repairs coverage',
      },
      {
        background: liabilityImg.src,
        title: 'If you’re sued<br/>for liability',
        description: 'We’ll help cover the costs of a lawyer to defend you.',
        alt: 'car liability insurance for lawyer expenses',
      },
      {
        background: theftImg.src,
        title: 'If your car <br/> is stolen',
        description: "We'll cover the cost to replace it with a new car.",
        alt: 'stolen car insurance',
      },
    ],
  },
  customersReviews: {
    title: "America's top-rated insurance,\nnow for your car",
    subtitle: '4.9 stars in the App store, and top-rated by Supermoney, Clearsurance, and others',
    darkBackground: true,
  },
  enableHeaderGreenButton: true,
  faq: {
    title: 'Good to know...',
    subtitle: `Car insurance can be confusing, so we worked hard to make it simple. If you\nstill have questions, you can check <a target="_blank" rel="noreferrer" href="https://www.lemonade.com/car/explained/">car insurance explained</a>`,
    variant: 'default',
    data: () => [
      {
        question: 'How do you help me clean up CO₂?',
        answer:
          'By planting trees! We learned that a typical car emits 4.6 metric tons of CO₂ each year, which is a lot. So we decided we’ll plant trees to help shrink your carbon footprint, using our app to determine the number of trees based on your mileage.',
        analytics: 'car_clean_co2',
      },

      {
        question: 'Can I trust you guys?',
        answer:
          'Of course. Unlike most of the new insurance startups out there, Lemonade is an insurance carrier, reinsured by some of the most trusted names in the industry. We’re a public company, traded on the NYSE, and rated A-Exceptional for financial stability.',
        analytics: 'car_fixed_trust',
      },
      {
        question: 'Do all drivers need the Lemonade app on their phone?',
        answer:
          'Depending on the state, they do. We use smart technology to price policies fairly, speed up claims, provide on-site emergency services, and measure driving impact—like CO₂ emissions so we can plant trees in your name.',
        analytics: 'car_fixed_app',
      },
      {
        question: 'Is it okay to disable location services for the Lemonade app?',
        answer:
          'In some states, not using the app can affect your price. Keep location on for the best rate, access to emergency services, a smoother claims experience, and more.',
        analytics: 'car_fixed_location_services',
      },
      {
        question: 'Can I switch now if I’m currently insured elsewhere?',
        answer:
          'Sure. You’ll need to send a cancellation request to your current carrier after you sign up for a Lemonade policy to make sure there’s no gaps in your coverage. Our team will be happy to help!',
        analytics: 'car_fixed_switch_now',
      },
      {
        question: 'What happens if I’m in an accident?',
        answer:
          'Once everyone’s safe, you’ll need to file a claim through our app. It takes a couple of minutes and is super simple- just answer a few questions, and follow the steps. We’ll reimburse you for repairs or replace your car on covered claims.',
        analytics: 'car_fixed_accident',
      },
      {
        question: 'I’m an Uber/Lyft driver, can I use your insurance?',
        answer:
          'Not yet. Our current coverage only works for drivers who don’t use their cars for business or commercial purposes. Sorry!',
        analytics: 'car_fixed_ridesharing',
      },
      {
        question: 'I’m buying a new car, how do I insure it?',
        answer:
          'Once you have a Lemonade Car policy it’s really easy to add a new car in the app or on our site. If you’re buying a new car when you open a policy we can provide same-day coverage - our team will be happy to help!',
        analytics: 'car_fixed_new_car',
      },
      {
        question: 'Can I edit my coverage after purchase?',
        answer:
          'No problem. You can edit your coverages by adjusting your limits and deductibles in the app or on our site whenever you’d like.',
        analytics: 'car_fixed_coverage',
      },
      {
        question: 'What happens if someone who’s not insured hits my car?',
        answer:
          'You’re covered. Most states require protection that guarantees you’re not left in a tough spot if an uninsured driver causes an accident, so your Lemonade Car policy will cover the costs of these damages in a covered claim.',
        analytics: 'car_fixed_uninsured_driver',
      },
      {
        question: 'How do you calculate my price?',
        answer:
          'We use a number of factors to calculate the most accurate and fair price, including your car’s model, as well as your location and driving history. In other words: Safe, low mileage drivers pay less.',
        analytics: 'car_fixed_price_calculation',
      },
      {
        question: 'What discounts do you have for eco-friendly cars?',
        answer:
          'We offer a discount for all electric and hybrid cars, plus we’ll cover you for emergency charging and damage to your wall charger.',
        analytics: 'car_fixed_eco_friendly_discounts',
      },
    ],
  },
};

export const embedConfig = {
  ...config,
  seo: {
    ...config.seo,
    advancedRobots: true,
    noIndex: true,
  },
};
